import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCanvasStore } from '../store/canvasStore';

const TextEditor: React.FC = () => {
  const isEditingTextId = useCanvasStore((s) => s.isEditingTextId);
  const setIsEditingTextId = useCanvasStore((s) => s.setIsEditingTextId);
  const texts = useCanvasStore((s) => s.texts);
  const setTexts = useCanvasStore((s) => s.setTexts);
  const editingStyle = useCanvasStore((s) => s.editingStyle);
  const inputRef = useCanvasStore((s) => s.inputRef);
  const setSelectedId = useCanvasStore((s) => s.setSelectedId);

  const [editingValue, setEditingValue] = useState('');
  const currentText = useMemo(() => texts.find((t) => t.id === isEditingTextId), [texts, isEditingTextId]);

  useEffect(() => {
    if (isEditingTextId && currentText) {
      setEditingValue(currentText.text);
    }
  }, [isEditingTextId, currentText]);

  useEffect(() => {
    if (isEditingTextId && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingTextId, inputRef]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newText = e.target.value;
      setEditingValue(newText);

      if (isEditingTextId) {
        setTexts((prev) => prev.map((t) => (t.id === isEditingTextId ? { ...t, text: newText } : t)));
      }
    },
    [isEditingTextId, setTexts]
  );

  const handleBlur = useCallback(() => {
    if (isEditingTextId) {
      setIsEditingTextId(null);
      setSelectedId(isEditingTextId);
    }
  }, [isEditingTextId, setIsEditingTextId, setSelectedId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleBlur();
      }
    },
    [handleBlur]
  );

  if (!isEditingTextId) return null;

  return (
    <div
      className="text-editor-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        pointerEvents: 'none',
      }}
    >
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={editingValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{
          position: 'absolute',
          top: `${editingStyle.top}px`,
          left: `${editingStyle.left}px`,
          width: `${editingStyle.width}px`,
          minHeight: `${editingStyle.height}px`,
          padding: '8px',
          border: '2px solid #3498db',
          borderRadius: '4px',
          outline: 'none',
          fontSize: `${currentText?.fontSize || 16}px`,
          lineHeight: `${currentText?.lineHeight || 1.5}`,
          fontFamily: `${currentText?.fontFamily || 'Arial'}`,
          color: currentText?.color || '#000000',
          resize: 'none',
          overflow: 'auto',
          background: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          zIndex: 1001,
          pointerEvents: 'auto',
        }}
      />
    </div>
  );
};

export default TextEditor;
